import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, Save, Settings, AlertTriangle, Check, Shield, Wrench, Download,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = (path, opts = {}) =>
  fetch(`${BACKEND}/api/implementation${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers },
  }).then((r) => {
    if (!r.ok) throw new Error(`API error ${r.status}`);
    return r.json();
  });

const READINESS_COLORS = {
  Ready: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  "Needs Clarification": "bg-amber-500/10 text-amber-500 border-amber-500/20",
  Blocked: "bg-red-500/10 text-red-500 border-red-500/20",
};

const BUILD_TYPES = ["Portal", "Agent", "Hybrid"];
const DELIVERY_MODES = ["AI Assistant", "Workflow Automation", "AI Agent", "Human-in-the-loop", "Reporting / Analytics"];
const RUNTIMES = ["Emergent Native", "OpenClaw Backed", "Twin Backed", "Manual / Advisory", "TBD"];
const COMPLEXITIES = ["Lite", "Standard", "Advanced"];
const READINESS_OPTS = ["Ready", "Needs Clarification", "Blocked"];

export default function WorkflowPackageReview({ tenantId }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportOpenClaw = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND}/api/export/tenants/${tenantId}/openclaw`);
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const data = await res.json();
      const companyName = (data.customer?.company_name || "tenant").replace(/[^a-zA-Z0-9]/g, "_");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `openclaw_handoff_${companyName}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("OpenClaw handoff exported");
    } catch (err) {
      toast.error("Export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      api(`/tenants/${tenantId}/packages`),
      api(`/tenants/${tenantId}/plan`),
    ]).then(([pkgs, planData]) => {
      setPackages(pkgs);
      setPlan(planData?.plan || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tenantId]);

  const updateField = (pkgId, field, value) => {
    setPackages((prev) => prev.map((p) => p.id === pkgId ? { ...p, [field]: value } : p));
  };

  const handleSave = async (pkg) => {
    setSaving(pkg.id);
    try {
      await api(`/tenants/${tenantId}/packages/${pkg.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          build_type: pkg.build_type,
          delivery_mode: pkg.delivery_mode,
          suggested_runtime: pkg.suggested_runtime,
          complexity: pkg.complexity,
          systems_involved: pkg.systems_involved,
          data_sources: pkg.data_sources,
          security_notes: pkg.security_notes,
          dependencies: pkg.dependencies,
          blockers: pkg.blockers,
          definition_of_done: pkg.definition_of_done,
          weekly_measurement_method: pkg.weekly_measurement_method,
          readiness_status: pkg.readiness_status,
        }),
      });
      toast.success("Package updated");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-foreground/30" /></div>;

  if (!packages.length) {
    return (
      <Card className="border-border bg-card p-8 text-center">
        <Settings className="h-8 w-8 text-foreground/15 mx-auto mb-3" />
        <div className="text-sm text-foreground/40">No workflow packages found for this tenant.</div>
      </Card>
    );
  }

  const SelectField = ({ label, value, onChange, options, testId }) => (
    <div>
      <Label className="text-xs text-foreground/60">{label}</Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8 w-full rounded-md border border-border bg-background text-xs text-foreground px-2" data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div data-testid="workflow-package-review">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Wrench className="h-4 w-4 text-foreground/40" />Workflow Package Review</h3>
          <div className="text-xs text-foreground/30 mt-0.5">Internal view — not visible to customers</div>
        </div>
        {plan && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">Plan Status: {plan.status}</Badge>
            <Button size="sm" variant="outline" onClick={handleExportOpenClaw} disabled={exporting} className="h-7 text-[10px] border-[var(--brand)]/30 text-[var(--brand)] hover:bg-[var(--brand)]/10" data-testid="export-openclaw-btn">
              {exporting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}Export to OpenClaw
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="border-border bg-card" data-testid={`package-${pkg.id}`}>
            <div className="p-4 border-b border-border/50 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-foreground">{pkg.workflow_name}</div>
                <div className="text-xs text-foreground/30">Internal Build Package</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] ${READINESS_COLORS[pkg.readiness_status] || READINESS_COLORS.Ready}`}>
                  {pkg.readiness_status || "Ready"}
                </Badge>
                <Button size="sm" variant="outline" onClick={() => handleSave(pkg)} disabled={saving === pkg.id} className="h-7 text-[10px]" data-testid={`save-pkg-${pkg.id}`}>
                  {saving === pkg.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <SelectField label="Build Type" value={pkg.build_type} onChange={(v) => updateField(pkg.id, "build_type", v)} options={BUILD_TYPES} testId={`pkg-build-${pkg.id}`} />
                <SelectField label="Delivery Mode" value={pkg.delivery_mode} onChange={(v) => updateField(pkg.id, "delivery_mode", v)} options={DELIVERY_MODES} testId={`pkg-delivery-${pkg.id}`} />
                <SelectField label="Suggested Runtime" value={pkg.suggested_runtime} onChange={(v) => updateField(pkg.id, "suggested_runtime", v)} options={RUNTIMES} testId={`pkg-runtime-${pkg.id}`} />
                <SelectField label="Complexity" value={pkg.complexity} onChange={(v) => updateField(pkg.id, "complexity", v)} options={COMPLEXITIES} testId={`pkg-complexity-${pkg.id}`} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-foreground/60">Systems Involved</Label>
                  <Input value={(pkg.systems_involved || []).join(", ")} onChange={(e) => updateField(pkg.id, "systems_involved", e.target.value.split(",").map((s) => s.trim()))} className="mt-1 h-8 text-xs border-border bg-background" />
                </div>
                <div>
                  <Label className="text-xs text-foreground/60">Data Sources</Label>
                  <Input value={(pkg.data_sources || []).join(", ")} onChange={(e) => updateField(pkg.id, "data_sources", e.target.value.split(",").map((s) => s.trim()))} className="mt-1 h-8 text-xs border-border bg-background" />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-foreground/60">Dependencies</Label>
                  <Input value={(pkg.dependencies || []).join(", ")} onChange={(e) => updateField(pkg.id, "dependencies", e.target.value.split(",").map((s) => s.trim()))} className="mt-1 h-8 text-xs border-border bg-background" />
                </div>
                <div>
                  <Label className="text-xs text-foreground/60">Blockers</Label>
                  <Input value={(pkg.blockers || []).join(", ")} onChange={(e) => updateField(pkg.id, "blockers", e.target.value.split(",").map((s) => s.trim()))} className="mt-1 h-8 text-xs border-border bg-background" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-foreground/60">Security / Compliance Notes</Label>
                <Textarea value={pkg.security_notes || ""} onChange={(e) => updateField(pkg.id, "security_notes", e.target.value)} className="mt-1 min-h-[40px] text-xs border-border bg-background" />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-foreground/60">Definition of Done</Label>
                  <Textarea value={pkg.definition_of_done || ""} onChange={(e) => updateField(pkg.id, "definition_of_done", e.target.value)} className="mt-1 min-h-[40px] text-xs border-border bg-background" />
                </div>
                <div>
                  <Label className="text-xs text-foreground/60">Weekly Measurement Method</Label>
                  <Textarea value={pkg.weekly_measurement_method || ""} onChange={(e) => updateField(pkg.id, "weekly_measurement_method", e.target.value)} className="mt-1 min-h-[40px] text-xs border-border bg-background" />
                </div>
              </div>

              <div className="w-40">
                <SelectField label="Readiness Status" value={pkg.readiness_status} onChange={(v) => updateField(pkg.id, "readiness_status", v)} options={READINESS_OPTS} testId={`pkg-readiness-${pkg.id}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
